import React, { useEffect, useRef, useState } from 'react';

type Props = {
  title: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
};

export default function FAQItem({ title, children, icon }: Props) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>('0px');

  useEffect(() => {
    if (open) {
      const height = contentRef.current?.scrollHeight ?? 0;
      setMaxH(`${height}px`);
    } else {
      setMaxH('0px');
    }
  }, [open]);

  return (
    <article className="relative rounded-[28px] border border-[var(--line-soft)] bg-white/92 shadow-[0_20px_45px_-38px_rgba(8,31,53,0.35)] backdrop-blur-sm">
      <div className="flex items-center gap-4 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--surface-3)] text-xl text-[var(--brand-accent)]" aria-hidden="true">
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="w-full text-left">
            <button
              aria-expanded={open}
              onClick={() => setOpen(v => !v)}
              className="flex w-full items-center justify-between"
            >
              <h3 className="text-base font-bold leading-7 text-[var(--brand-ink)]">{title}</h3>
              <span className={`ml-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--line-soft)] bg-[var(--surface-0)] text-sm text-[var(--brand-accent)] transition-transform duration-200 ${open ? 'rotate-45' : 'rotate-0'}`}>
                {open ? '−' : '+'}
              </span>
            </button>

            <div
              ref={contentRef}
              style={{ maxHeight: maxH, transition: 'max-height 320ms ease' }}
              className="overflow-hidden"
            >
              <div className={`mt-3 pr-4 text-sm leading-7 text-[var(--text-muted)] transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
